import React from 'react';
import { ClockIcon } from 'lucide-react';
import { useWeeklyPromo } from '../hooks/useWeeklyPromo';
interface PromoCountdownProps {
  className?: string;
  /** Compact inline variant for bars and tight cards. */
  compact?: boolean;
  /** Boxed block sized for offer cards, placed under the buy button. */
  variant?: 'default' | 'card';
}
const pad = (n: number) => String(n).padStart(2, '0');

/** Live countdown to Sunday 11:59:59 PM ET. Fixed-width digits so nothing shifts as it ticks. */
export function PromoCountdown({
  className = '',
  compact = false,
  variant = 'default'
}: PromoCountdownProps) {
  const promo = useWeeklyPromo();
  const units = [{
    label: 'Days',
    value: promo.days
  }, {
    label: 'Hrs',
    value: promo.hours
  }, {
    label: 'Min',
    value: promo.minutes
  }, {
    label: 'Sec',
    value: promo.seconds
  }];

  // "This week's 250 Years price ends Sunday" → "Sale ends Sunday", sized for a card.
  const shortHeadline = `Sale ends ${promo.headline.split(' ').pop()}`;
  if (variant === 'card') {
    return <p className={`flex items-center justify-center gap-2 whitespace-nowrap text-center ${className}`} role="timer" aria-label={`${promo.headline}. ${promo.days} days, ${promo.hours} hours, ${promo.minutes} minutes left`} data-id="element-741" data-mp="3j3agr8ualcqc">
        <ClockIcon className="h-3.5 w-3.5 shrink-0 text-grit-500" aria-hidden="true" data-id="element-742" data-mp="qcbqnin7tpb" />
        <span className="text-[11px] font-extrabold uppercase tracking-[0.14em] text-grit-500" aria-hidden="true" data-id="element-743" data-mp="304xxuvdkwohk">
          {shortHeadline}
        </span>
        <span className="text-[12px] font-bold tabular-nums text-offwhite" aria-hidden="true" data-id="element-744" data-mp="3471ioam515fi">
          {pad(promo.days)}d {pad(promo.hours)}h {pad(promo.minutes)}m {pad(promo.seconds)}s
        </span>
      </p>;
  }
  if (compact) {
    return <span className={`inline-flex items-center gap-2 ${className}`} data-id="element-745" data-mp="ofnda77ths4i">
        <span data-id="element-746" data-mp="ohrhkq9mot5b">{shortHeadline}</span>
        <span className="font-mono tabular-nums" aria-hidden="true" data-id="element-747" data-mp="tdyn4hy442mq">
          {pad(promo.days)}:{pad(promo.hours)}:{pad(promo.minutes)}:{pad(promo.seconds)}
        </span>
      </span>;
  }
  return <div className={className} data-id="element-748" data-mp="3uxf6z0vnu2k0">
      <p className="text-[11px] font-extrabold uppercase tracking-[0.22em] text-grit-500" data-id="element-749" data-mp="3uw491howbp0t">
        {promo.headline}
        {promo.subline && <span className="ml-2 text-offwhite" data-id="element-750" data-mp="8pfn60j9yekx">· {promo.subline}</span>}
      </p>
      <div className="mt-3 flex items-start gap-2 sm:gap-3" role="timer" aria-label={`${promo.days} days, ${promo.hours} hours, ${promo.minutes} minutes left`} data-id="element-751" data-mp="8ncn0noer52h">
        {units.map((unit, index) => <React.Fragment key={unit.label} data-id="element-752" data-mp="3qusfx26tclnv" data-mpi={index}>
            {index > 0 && <span className="pt-2 font-display text-2xl font-bold leading-none text-steel-500 sm:text-3xl" aria-hidden="true" data-id="element-753" data-mp="3qwveiam17n47">
                :
              </span>}
            <span className="flex w-14 flex-col items-center border border-white/12 bg-ink-900 py-2 sm:w-16" aria-hidden="true" data-id="element-754" data-mp="2ycwvhc09tujx">
              <span className="font-display text-3xl font-extrabold leading-none tabular-nums text-offwhite sm:text-4xl" data-id="element-755" data-mp="18xm0ssz5q9jq">
                {pad(unit.value)}
              </span>
              <span className="mt-1 text-[10px] font-bold uppercase tracking-[0.18em] text-steel-400" data-id="element-756" data-mp="1dqf9frxqy213">{unit.label}</span>
            </span>
          </React.Fragment>)}
      </div>
    </div>;
}